import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import toast from "react-hot-toast";

type Link = {
  id: string;
  token: string;
  label: string | null;
  expires_at: string | null;
  max_uses: number | null;
  uses: number;
  revoked_at: string | null;
};

/**
 * The link a coach sends to a group of parents.
 *
 * This is the way clubs actually fill a team: one message in the WhatsApp
 * group, and the parents do the rest. Typing thirty email addresses is the
 * fallback, not the plan — so this is one tap, the share sheet opens with the
 * message already written, and the link is created on demand rather than
 * behind a form nobody would fill in.
 *
 * Everything it creates still waits for the club's approval, which is what
 * makes it safe to paste somewhere we do not control.
 */
export default function TeamInviteLink({
  teamId,
  teamName,
  clubName,
  compact = false,
}: {
  teamId: string;
  teamName?: string | null;
  clubName?: string | null;
  compact?: boolean;
}) {
  const [link, setLink] = useState<Link | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const url = link ? `${window.location.origin}/join/${link.token}` : "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<{ links: Link[] }>(`/join-links/team/${teamId}`);
      // The one still worth sharing: not revoked, not expired, not used up.
      const now = Date.now();
      const usable = (data.links || []).find(
        (l) =>
          !l.revoked_at &&
          (!l.expires_at || new Date(l.expires_at).getTime() > now) &&
          (l.max_uses == null || l.uses < l.max_uses)
      );
      setLink(usable || null);
    } catch {
      setLink(null);
    }
    setLoading(false);
  }, [teamId]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post<{ link: Link }>(`/join-links/team/${teamId}`, { days: 30 });
      setLink(data.link);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut genera linkul.");
    }
    setCreating(false);
  };

  const message = [
    `Bună! Vă invit să vă înscrieți copilul în echipa ${teamName || ""}`,
    clubName ? ` a clubului ${clubName}` : "",
    ".\n\nCompletați aici, durează două minute:\n",
    url,
  ].join("");

  const share = async () => {
    // The share sheet is the point: it puts WhatsApp first on a phone, which
    // is where these parents already are.
    if (navigator.share) {
      try {
        await navigator.share({ title: teamName || "Înscriere în echipă", text: message });
        return;
      } catch {
        // Cancelled, or unavailable — fall through to copying.
      }
    }
    await copy();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiat. Lipește-l în grupul părinților.");
    } catch {
      toast.error("Nu am putut copia. Ține apăsat pe link și copiază manual.");
    }
  };

  if (loading) {
    return <p className="text-sm text-slate-400">Se încarcă linkul...</p>;
  }

  if (!link) {
    return (
      <div className={compact ? "" : "p-4 bg-indigo-50 rounded-2xl"}>
        <p className="text-sm font-bold text-slate-800">Invită prin link</p>
        <p className="mt-1 mb-3 text-xs leading-relaxed text-slate-600">
          Generezi un link și îl trimiți în grupul părinților. Ei completează datele, tu confirmi.
        </p>
        <button
          type="button"
          onClick={create}
          disabled={creating}
          className="w-full px-4 py-3 text-sm font-extrabold text-white bg-indigo-600 rounded-2xl disabled:opacity-60"
        >
          {creating ? "Se generează..." : "Generează linkul echipei"}
        </button>
      </div>
    );
  }

  return (
    <div className={compact ? "" : "p-4 bg-indigo-50 rounded-2xl"}>
      <p className="text-sm font-bold text-slate-800">Invită prin link</p>
      <p className="mt-1 text-xs text-slate-600">
        Trimite-l în grupul părinților. Fiecare cerere ajunge la tine spre confirmare.
      </p>

      <div className="px-3 py-2 mt-3 overflow-hidden text-xs bg-white border rounded-xl border-indigo-100 text-slate-500">
        <span className="break-all">{url}</span>
      </div>

      <div className="flex gap-2 mt-3">
        <button
          type="button"
          onClick={share}
          className="flex-1 px-4 py-3 text-sm font-extrabold text-white bg-indigo-600 rounded-2xl"
        >
          Trimite părinților
        </button>
        <button
          type="button"
          onClick={copy}
          className="px-4 py-3 text-sm font-bold bg-white border text-slate-700 border-slate-200 rounded-2xl"
        >
          Copiază
        </button>
      </div>

      <p className="mt-2 text-[11px] text-slate-400">
        {link.uses} {link.uses === 1 ? "înscriere" : "înscrieri"} prin acest link
        {link.expires_at
          ? ` · expiră pe ${new Date(link.expires_at).toLocaleDateString("ro-RO")}`
          : ""}
      </p>
    </div>
  );
}
